import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface FaqListItem {
  answer: string;
  /** Optional deep-link target for an individual question. */
  id?: string;
  question: string;
}

interface FaqListProps {
  className?: string;
  heading: ReactNode;
  /** A page-stable id used to associate the section with its heading. */
  headingId: string;
  items: readonly FaqListItem[];
  /** Renders the inline grammar used by guides, tools, and rich FAQ answers. */
  renderText?: (text: string) => ReactNode;
}

function renderPlainText(text: string): ReactNode {
  return text;
}

/**
 * The visible FAQ design-system primitive.
 *
 * Structured data deliberately stays with the page that owns the FAQ source. This
 * component owns only the shared semantics and presentation, so composing it cannot
 * accidentally emit a second FAQPage node.
 */
export function FaqList({
  className,
  heading,
  headingId,
  items,
  renderText = renderPlainText,
}: FaqListProps) {
  return (
    <section aria-labelledby={headingId} className={cn("space-y-4", className)}>
      <h2
        className="scroll-mt-24 font-semibold text-foreground text-xl"
        id={headingId}
      >
        {heading}
      </h2>
      <dl className="space-y-4">
        {items.map((item) => (
          <div
            className={cn(
              "min-w-0 rounded-lg border border-border bg-card p-5",
              item.id && "scroll-mt-24"
            )}
            id={item.id}
            key={item.id ?? item.question}
          >
            <dt className="break-words font-medium text-foreground">
              {renderText(item.question)}
            </dt>
            <dd className="mt-2 break-words text-muted-foreground text-sm leading-relaxed">
              {renderText(item.answer)}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
